import React, { useCallback, useContext, useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { accessLocalStorage, exportSelectAllQueueMembers, exportDeleteQueueMember } from '../../../services';
import { scopeByTenant } from '../../../services/tenantScope';
import { hasFeature } from '../../../services/permissions';
import MembersTable from './components/MembersTable';

import PbxContext from '../../../context/PbxContext';
import '../../../libs/bulma.min.css';
import './MembersList.css';
import './QueuesTable.css';

import Loading from '../../Loading/LoadingModule';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';

function MembersQueuesList() {
  const getItensStateGlobal = useContext(PbxContext);
  const { setItemsSelectedToEdit } = getItensStateGlobal;

  const [loading, setLoading] = useState(true);
  const [sortElements, setSortElements] = useState({ key: '', direction: 'ascending'});
  const [membersQueues, setMembersQueues] = useState(() => []);
  const [membersQueuesSelected, setMembersQueuesSelected] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  const history = useHistory();
  const canEdit = hasFeature('config.filas', 'edit');

  const fetchMemberQueues = async (idtenant) => {
    try {
      const membersQueuesList = await exportSelectAllQueueMembers();

      if(membersQueuesList === undefined || !Array.isArray(membersQueuesList)) {
        setLoading(false);
        setMembersQueues([]);
        return true;
      }

      let elements = membersQueuesList.map((element) => {
        const queueNameValue = element.queuename !== null && element.queuename !== undefined
          ? String(element.queuename).trim()
          : '';

        return {
          ...element,
          queueName: queueNameValue,
          endpoint: element.endpoint ? String(element.endpoint) : '',
          membername: element.membername || '',
          accountcode: element.accountcode || ''
        };
      });

      elements = scopeByTenant(elements, (element) => element.accountcode, idtenant);

      setMembersQueues(elements);
      setLoading(false);
      return true;
    } catch (error) {
      setLoading(false);
      setMembersQueues([]);
      return false;
    }
  };

  const validateUserLogged = useCallback(async () => {
    const dataUser = accessLocalStorage.getUserLocalStorage();
    if (!dataUser) return history.push('/');
    const { user: { idtenant } } = dataUser;
    const listQueuesMembers = await fetchMemberQueues(idtenant);
    return listQueuesMembers;
  }, []);

  const handleChangeSelectedMembersQueues = (element) => {
    const uniqueid = Number(element);
    setMembersQueuesSelected((prevState) => (
      prevState.includes(uniqueid)
        ? prevState.filter((id) => id !== uniqueid)
        : [...prevState, uniqueid]
    ));
  };

  const handleClickDelete = async () => {
    setDeleting(true);
    setAlert({ show: false, type: '', message: '' });

    const result = await exportDeleteQueueMember({ id: membersQueuesSelected });

    setDeleting(false);
    setConfirmDelete(false);

    if (!result.success) {
      setAlert({ show: true, type: 'danger', message: result.error });
      return;
    }

    const deletedCount = result.data && Number.isFinite(result.data.deletedCount)
      ? result.data.deletedCount
      : membersQueuesSelected.length;
    setAlert({ show: true, type: 'success', message: `${deletedCount} membro(s) removido(s) da fila.` });
    setMembersQueuesSelected([]);
    await validateUserLogged();
  };

  const handleClickUpdate = async () => {
    if (membersQueuesSelected.length !== 1) return;
    setItemsSelectedToEdit(membersQueuesSelected[0]);
    return history.push('/config/fila/membros/lista/id');
  };

  useMemo(() => {
    let sortableItems = membersQueues;
    if (sortElements !== null) {
      sortableItems.sort((a, b) => {
        if(sortElements.key === 'endpoint' && sortElements.direction === 'ascending') return a[sortElements.key] - b[sortElements.key];
        if(sortElements.key === 'endpoint' && sortElements.direction === 'descending') return b[sortElements.key] - a[sortElements.key];

        if (a[sortElements.key] < b[sortElements.key]) {
          return sortElements.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortElements.key] > b[sortElements.key]) {
          return sortElements.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }

    setMembersQueues(sortableItems);
    return;
  }, [sortElements]);

  const sortedCalls = (key) => {
    let newDirection = sortElements.direction;
    if (sortElements.key === '' || sortElements.key !== key) {
      newDirection = 'descending';

      setSortElements((prevState) => prevState = { key, direction: newDirection });
      return;
    }
    if (sortElements.key === key && sortElements.direction === 'ascending') {
      newDirection = 'descending';
    } else {
      newDirection = 'ascending';
    }
    setSortElements((prevState) => prevState = { key, direction: newDirection });
    return;
  };

  const filteredMembers = membersQueues.filter((member) => {
    const searchText = filterText.toLowerCase();
    return (
      (member.endpoint && member.endpoint.toString().includes(searchText)) ||
      (member.membername && member.membername.toLowerCase().includes(searchText)) ||
      (member.queueName && member.queueName.toLowerCase().includes(searchText))
    );
  });

  const filteredIds = filteredMembers.map((member) => Number(member.uniqueid));
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => membersQueuesSelected.includes(id));
  const canDelete = membersQueuesSelected.length >= 1;
  const canUpdate = membersQueuesSelected.length === 1;

  const handleChangeSelectAll = () => {
    if (allFilteredSelected) {
      setMembersQueuesSelected((prevState) => prevState.filter((id) => !filteredIds.includes(id)));
      return;
    }
    setMembersQueuesSelected((prevState) => {
      const next = new Set(prevState);
      filteredIds.forEach((id) => next.add(id));
      return [...next];
    });
  };

  useEffect(() => {
    let cancel = false;

    if (cancel) return;
    validateUserLogged();

    return () => {
      cancel = true
    };
  }, [validateUserLogged]);

  return (
    <div className="members-list-container">
      {
        loading ?
          <Loading />
        :
        <div>
          <div className="members-list-header">
            <div className="members-list-title-wrapper">
              <svg className="members-list-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <div>
                <h1 className="members-list-title">Lista de Membros</h1>
                <p className="members-list-subtitle">Gerencie os membros dos grupos atendedores</p>
              </div>
            </div>
            {canEdit && !confirmDelete && (
              <div id="buttons" className="queues-actions">
                <button className="btn-queue-action btn-view-queue" type="submit" onClick={() => handleClickUpdate()} disabled={!canUpdate}>
                  <FontAwesomeIcon icon={faPen} />
                  <span>Alterar</span>
                </button>
                <button
                  className="btn-queue-action btn-delete-queue"
                  type="submit"
                  onClick={() => setConfirmDelete(true)}
                  disabled={!canDelete}
                >
                  <FontAwesomeIcon icon={faTrash} />
                  <span>Deletar</span>
                </button>
              </div>
            )}
          </div>

          {alert.show && (
            <div className="alerts-section">
              <div className={`alert-message alert-${alert.type}`}>
                <div className="alert-header">
                  <span>{alert.type === 'success' ? 'Sucesso!' : 'Erro!'}</span>
                  <button className="alert-close" aria-label="delete" onClick={() => setAlert({ show: false, type: '', message: '' })}>×</button>
                </div>
                <div className="alert-body">
                  {alert.message}
                </div>
              </div>
            </div>
          )}

          {confirmDelete && (
            <div className="alerts-section">
              <div className="alert-message alert-danger">
                <div className="alert-header">
                  <span>Atenção!</span>
                  <button className="alert-close" aria-label="delete" onClick={() => setConfirmDelete(false)}>×</button>
                </div>
                <div className="alert-body">
                  {membersQueuesSelected.length === 1
                    ? 'Tem certeza que quer excluir 1 membro selecionado?'
                    : `Tem certeza que quer excluir ${membersQueuesSelected.length} membros selecionados?`}
                  <div className="alert-actions">
                    <button className="btn-alert btn-alert-confirm" type="submit" onClick={() => handleClickDelete()} disabled={deleting}>
                      <FontAwesomeIcon icon={faTrash} />
                      <span>{deleting ? 'Excluindo...' : 'Confirmar'}</span>
                    </button>
                    <button className="btn-alert btn-alert-cancel" type="submit" onClick={() => setConfirmDelete(false)} disabled={deleting}>
                      <FontAwesomeIcon icon={faTrash} />
                      <span>Cancelar</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="filter-section">
            <div className="filter-input-wrapper">
              <svg className="filter-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                className="filter-input"
                placeholder="Filtrar por ramal, nome do atendente ou fila..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
              {filterText && (
                <button className="filter-clear" onClick={() => setFilterText('')}>
                  ×
                </button>
              )}
            </div>
            <div className="filter-results">
              Exibindo {filteredMembers.length} de {membersQueues.length} membro(s)
            </div>
          </div>

          <div className="queues-table-wrapper">
            <table className="queues-table">
              <thead className="queues-table-header">
                <tr>
                  <th className="checkbox-cell">
                    <input
                      id="select-all-members"
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={() => handleChangeSelectAll()}
                    />
                  </th>
                  <th onClick={() => sortedCalls('endpoint')} style={{cursor: 'pointer'}}>
                    Ramal <FontAwesomeIcon icon={sortElements.key === 'endpoint' && sortElements.direction === 'descending' ? faArrowUp : faArrowDown} />
                  </th>
                  <th onClick={() => sortedCalls('membername')} style={{cursor: 'pointer'}}>
                    Nome Atendente <FontAwesomeIcon icon={sortElements.key === 'membername' && sortElements.direction === 'descending' ? faArrowUp : faArrowDown} />
                  </th>
                  <th onClick={() => sortedCalls('queueName')} style={{cursor: 'pointer'}}>
                    Nome da Fila <FontAwesomeIcon icon={sortElements.key === 'queueName' && sortElements.direction === 'descending' ? faArrowUp : faArrowDown} />
                  </th>
                  <th>Pausa</th>
                  <th>Prioridade</th>
                  <th>Sequência</th>
                  <th>Toca em Uso</th>
                  <th>Estado</th>
                  <th>Descanso</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.map((member) => (
                  <MembersTable
                    key={member.uniqueid}
                    member={member}
                    selectMember={handleChangeSelectedMembersQueues}
                    isSelected={membersQueuesSelected.includes(Number(member.uniqueid))}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      }
    </div>
  );
}

export default MembersQueuesList;
